import React, { useCallback, useEffect, useRef, useState } from 'react';
import './iFrame.scss';

import debounce from 'lodash.debounce';
import { ApiService } from '../../../services/api-service';
import { container } from 'tsyringe';
import { useDispatch, useSelector } from 'react-redux';
import { getBadgeColor, getInitials, pickTextColorBasedOnBgColorAdvanced, useSharedState } from '../../../utils/helpers';

import * as R from 'ramda';
import { TemplateService } from '../../../services/template-service';
import { TemplatePosition, TemplateType } from '../../../models/templateClass';
import { ChatService } from '../../../services/chat-service';

export const IFrame = React.memo(function Start() {

  // Services initialization
  const [apiService] = useState(container.resolve(ApiService));
  const [templateService] = useState(container.resolve(TemplateService));
  const [chatService] = useState(container.resolve(ChatService));

  const [nameTags] = useSharedState(templateService.nametags$);
  const [logos] = useSharedState(templateService.logos$);
  const [publicChats] = useSharedState(chatService.publicChatMessages$);

  const templatesInStore = useSelector((state: any) => state.templates);


  let tickerInterval = null;
  const tickerIntervalRef = useRef(tickerInterval);

  let currentLeftPadding = 0;
  const leftPaddingRef = useRef(currentLeftPadding);
  // const [currentLeftPadding, setCurrentLeftPadding] = useState(60);

  let lowerthirdTheme = { theme: '', primary: '', secondary: '' };
  const lowerthirdThemeRef = useRef(lowerthirdTheme);

  const cornerPosition = [
    { pos: TemplatePosition.TOP_LEFT, coord: { x: 0.08, y: 0.08 } },
    { pos: TemplatePosition.TOP_RIGHT, coord: { x: 0.92, y: 0.08 } },
    { pos: TemplatePosition.BOTTOM_LEFT, coord: { x: 0.08, y: 0.92 } },
    { pos: TemplatePosition.BOTTOM_RIGHT, coord: { x: 0.92, y: 0.92 } },
    { pos: TemplatePosition.RIGHT, coord: { x: 0.92, y: 0.5 } },
    { pos: TemplatePosition.LEFT, coord: { x: 0.08, y: 0.5 } },
    { pos: TemplatePosition.BOTTOM, coord: { x: 0.5, y: 0.92 } },
    { pos: TemplatePosition.TOP, coord: { x: 0.5, y: 0.08 } },
  ]

  const sortOnStreamDate = (a, b) => {
    // reverse order
    return b.onStreamDate - a.onStreamDate;
  }


  const handleRunInAnimation = async () => {
    // console.log('in runAnimationIN');

    const attributes = [];

    const theme = (templatesInStore?.brandsettings?.selectedTheme || '1').replace(/lower_third/ig, '');
    const primaryColor = templatesInStore?.brandsettings?.primaryColor;
    const selectedColor = templatesInStore?.selectedLowerThird?.primaryColor;

    const LTprimary = templatesInStore?.selectedLowerThird?.primaryText || '';
    const LTsecondary = templatesInStore?.selectedLowerThird?.secondaryText || '';
    const isNewLT = (lowerthirdThemeRef.current.theme !== theme ||
      lowerthirdThemeRef.current.primary !== LTprimary ||
      lowerthirdThemeRef.current.secondary !== LTsecondary);

    lowerthirdThemeRef.current = { theme, primary: LTprimary, secondary: LTsecondary };

    let chatIndex = 0;
    let displayedChats = publicChats.filter(m => m.onStreamDate);
    // if (displayedChats.length > 0 && !templatesInStore?.selectedLowerThird) {
    if (displayedChats.length > 0) {
        // sort on reverse onStreamDate - most recent on bottom
      displayedChats.sort(sortOnStreamDate);
      for (const chat of displayedChats) {
        const yposition = 0.80 - (chatIndex++ / 8);
        if (yposition > 0) {
          const chatTag = {
            type: TemplateType.T_CHAT,
            theme: theme,
            number: chat.id,
            isVisible: true,
            animate: false,
            primaryColor: templatesInStore?.selectedTicker?.primaryColor,
            fgColor: "#4a4a4a",
            bgColor: 'transparent',
            borderColor: '#8d8d8d',

            fontFamily: 'Proxima Nova',
            fontWeight: 'bold',
            primaryText: chat.sender.name,
            secondaryText: chat.body,
            badgeColor: getBadgeColor(chat.sender.name),
            initials: getInitials(chat.sender.name),
            position: {
              x: 0.02,
              y: yposition,
            },
          }
          attributes.push(chatTag);
        }
      }
    } else {
      attributes.push({
        type: TemplateType.T_CHAT,
        theme: theme,
        number: 0,
        isVisible: false,
        position: { x: 0, y: 0 }
      });
    }

    attributes.push({
      type: TemplateType.T_LOWERTHIRD,
      theme: theme,
      number: '',
      isNew: isNewLT,
      isVisible: templatesInStore?.selectedLowerThird && displayedChats.length === 0 ? true : false,
      animate: true,
      primaryColor: selectedColor,
      fgColor: theme === '1' ? pickTextColorBasedOnBgColorAdvanced(selectedColor, '#FFFFFF', '#4a4a4a') : (theme === '2' ? '#ffffff' : '#4a4a4a'),
      bgColor: theme === '1' ? selectedColor : (theme === '2' ? 'transparent' : 'white'),
      borderColor: theme === '1' ? 'transparent' : selectedColor,
      fontFamily: 'Proxima Nova',
      fontSize: '2vh',
      // fontWeight: theme === '1' ? 'normal' : 'bold',
      fontWeight: 'bold',
      id: templatesInStore?.selectedLowerThird?.id,
      primaryText: LTprimary,
      secondaryText: LTsecondary,
      position: {
        x: 0.05,
        y: (templatesInStore?.selectedTicker ? 0.80 : 0.85)
      }
    });

    attributes.push({
      type: TemplateType.T_TICKER,
      theme: '',
      number: '',
      isVisible: templatesInStore?.selectedTicker ? true : false,
      animate: false,
      primaryColor: templatesInStore?.selectedTicker?.primaryColor,
      fgColor: pickTextColorBasedOnBgColorAdvanced(templatesInStore?.selectedTicker?.primaryColor, '#FFFFFF', '#4a4a4a'),
      bgColor: templatesInStore?.selectedTicker?.primaryColor,
      borderColor: 'transparent',
      fontFamily: 'Proxima Nova',
      fontSize: '3vh',
      fontWeight: 'normal',
      id: templatesInStore?.selectedTicker?.id,
      primaryText: templatesInStore?.selectedTicker?.primaryText || '',
      secondaryText: '',
      position: {
        x: 0,
        y: 0.95
      }
    });


    for (const lg of logos) {
      const position = cornerPosition.find(p => p.pos === lg.position);
      attributes.push({ ...lg, position: position.coord, positionName: lg.position, isVisible: lg.selected });
    }


    for (const nt of nameTags) {
      const ntWithTheme = {
        ...nt,
        theme: theme, // force theme , the one inside 'nt' may not be up to date yet
        fgColor: theme === '3' ? '#4a4a4a' :
        theme === '2' ?  '#FFFFFF' : pickTextColorBasedOnBgColorAdvanced(primaryColor, '#FFFFFF', '#4a4a4a'),
        bgColor: theme === '1' ? primaryColor : (theme === '2' ? 'transparent' : 'white'),
        borderColor: theme === '1' ? 'transparent' : primaryColor,
        // fontWeight: theme === '1' ? 'normal' : 'bold',
        fontWeight: 'bold',
        // position: { x: nt.position.x, y: nt.position.y + 0.02}
      }

      // console.log('NAME TAG', ntWithTheme);

      // hide name tags (except 0) when a lowerthird is active
      if (ntWithTheme.number === 0 ||
        (templatesInStore?.brandsettings.nametagsActive && !templatesInStore?.selectedLowerThird && displayedChats.length === 0)) {
        attributes.push(ntWithTheme);
      }
    }


    // push attributes into apiService
    apiService.templateAttributes$.next({ attributes, update: 0 });
    apiService.saveChannel('iframe');


    // run init fct inside iframe
    const iframe = document.getElementById('common_template') as HTMLIFrameElement;
    if (iframe) {
      initializeFromData(iframe.contentWindow.document, attributes);
    }
  }

  //   function measureText(pText) {
  //     let lDiv = document.createElement('div');
  //     document.body.appendChild(lDiv);

  //     lDiv.classList.add(TemplateType.T_CHAT + '-body');
  //     lDiv.innerHTML = pText;

  //     var lResult = {
  //         width: lDiv.clientWidth,
  //         height: lDiv.clientHeight
  //     };

  //     document.body.removeChild(lDiv);
  //     lDiv = null;

  //     return lResult;
  // }

  const initializeFromData = (document, data) => {

    const nametagsIds = data.filter(t => t.type === 'NAMETAG' && t.number !== 0).map(t => t.type + t.number + '-container');
    const chatsIds = data.filter(t => t.type === 'CHAT' && t.number !== 0).map(t => t.type + t.number + '-container');

    const logosIds = data.filter(t => t.type === 'LOGO').map(t => t.type + t.id + '-container');

    let deltaY = 0;

    const iframeWindow = document.getElementById('main_window') as HTMLDivElement;
    if (iframeWindow) {
      const width = iframeWindow.offsetWidth;
      const height = iframeWindow.offsetHeight;


      for (const template of data) {
        // console.log(template)

        // get container from id
        let prefix = template.type;

        let container = document.getElementById(prefix + '-container');
        const ntContainer = container;
        if (container) {

          if (template.type === TemplateType.T_LOWERTHIRD || template.type === TemplateType.T_TICKER) {
            if (!template.isVisible) {
              container.style.opacity = '0';
            } else {
              setTimeout(() => {
                container.style.opacity = '1';
              }, 200);
              if (template.type === TemplateType.T_LOWERTHIRD && template.isNew) {
                // console.log('theme change');
                container.style.width = '0';
                container.style.display = 'none';
              }
            }
          } else if (template.type === TemplateType.T_NAMETAG) {
            // NAMETAG type
            prefix += template.number;
            // get child list
            const child = container.children;
            for (let i = 0; i < child.length; i++) {
              // console.log('##NAMETAG length ', child.length);
              // check if id is still in list
              // console.log('##NAMETAG check ', child[i].id);
              // if not remove
              if (nametagsIds.length === 0 || !nametagsIds.includes(child[i].id)) {
                // console.log('##NAMETAG remove ', child[i].id);
                child[i].remove();
                i--; // child is updated and length is updated , current element is shifted out
              }
            }

            // check if nametag div already exist
            let ntc = document.getElementById(prefix + '-container');
            if (template.number !== 0) {
              if (ntc) {
                ntc.style.top = (template.position.y * 100) + '%';
                ntc.style.left = (template.position.x * 100) + '%';
                // ntc.style.transform = "translate3d(" + (template.position.x * width) + "px, " + (template.position.y * height) + "px, 0)";
                ntc.style.display = template.isVisible ? 'block' : 'none';

              } else {
                // add ntc and nt div
                ntc = document.createElement("div");
                ntc.id = prefix + '-container';
                ntc.classList.add(TemplateType.T_NAMETAG);
                ntc.style.top = '0%';
                ntc.style.left = '110%';
                // ntc.style.transform = "translate3d(" + width + "px, 0, 0)";
                ntc.style.display = template.isVisible ? 'block' : 'none';

                const nt = document.createElement("div");
                nt.id = prefix + '-primaryText';
                nt.classList.add(TemplateType.T_NAMETAG + '-text');
                ntc.appendChild(nt);

                // console.log(ntc.id + ' append to' + ntContainer.id);
                ntContainer.appendChild(ntc);

                ntc.style.top = (template.position.y * 100) + '%';
                ntc.style.left = (template.position.x * 100) + '%';
                // ntc.style.transform = "translate3d(" + (template.position.x * width) + "px, " + (template.position.y * height) + "px, 0)";

              }
              container = ntc;
            }
          } else if (template.type === TemplateType.T_LOGO) {
            // LOGO type
            prefix += template.id;
            // get child list
            const child = container.children;
            for (let i = 0; i < child.length; i++) {
              // if not remove
              if (logosIds.length === 0 || !logosIds.includes(child[i].id)) {
                // console.log('##LOGO remove ', child[i].id);
                child[i].remove();
                i--; // child is updated and length is updated , current element is shifted out
              }
            }

            // check if logo div already exist
            let ntc = document.getElementById(prefix + '-container');
            if (template.number !== 0) {
              if (ntc) {
                ntc.style.top = (template.position.y * 100) + '%';
                ntc.style.left = (template.position.x * 100) + '%';
                ntc.style.display = template.isVisible ? 'block' : 'none';
              } else {
                // add ntc and nt div
                ntc = document.createElement("div");
                ntc.id = prefix + '-container';
                ntc.classList.add(TemplateType.T_LOGO);
                ntc.style.top = (template.position.y * 100) + '%';
                ntc.style.left = (template.position.x * 100) + '%';
                ntc.style.display = template.isVisible ? 'block' : 'none';

                const nt = document.createElement("div");
                nt.id = prefix + '-img';
                nt.classList.add(TemplateType.T_LOGO + '-img');
                ntc.appendChild(nt);

                // console.log(ntc.id + ' append to' + ntContainer.id);
                ntContainer.appendChild(ntc);
              }
              container = ntc;
            }
          } else if (template.type === TemplateType.T_CHAT) {
            // CHAT type
            prefix += template.number;
            // get child list
            const child = container.children;
            for (let i = 0; i < child.length; i++) {
              child[i].style.top = '110%';
            }
            setTimeout(() => {
              for (let i = 0; i < child.length; i++) {
                // console.log('##CHAT length ', child.length);
                // check if id is still in list
                // console.log('##CHAT check ', child[i].id);
                // if not remove
                if (chatsIds.length === 0 || !chatsIds.includes(child[i].id)) {
                  // console.log('##CHAT remove ', child[i].id);
                  child[i].remove();
                  i--; // child is updated and length is updated , current element is shifted out
                }
              }
            }, 150);

            // check if chat div already exist
            let chatc = document.getElementById(prefix + '-container');
            if (template.number !== 0) {

              const charPerLine = 50;
              // 58 is max line length display on the body chat div ....   TODO compute automatically this value
              if (template.secondaryText.length > charPerLine) {
                const nblines = Math.floor(template.secondaryText.length / charPerLine);
                deltaY -= 0.0301 * nblines;
              }


              if (chatc) {
                chatc.style.top = ((template.position.y + deltaY) * 100) + '%';
                chatc.style.left = (template.position.x * 100) + '%';

                // chatc.style.transform = "translate3d("+ (template.position.x * width) +"px, " + (template.position.y * height) + "px, 0)";
                chatc.style.display = template.isVisible ? 'block' : 'none';
                // console.log('change pos', template.position.y, ((template.position.y + deltaY) * height));

              } else {
                // add chatc and chat div
                chatc = document.createElement("div");
                chatc.id = prefix + '-container';
                chatc.classList.add(TemplateType.T_CHAT);
                // chatc.style.transform = "translate3d(0, " + height + "px, 0)";
                chatc.style.display = template.isVisible ? 'block' : 'none';
                chatc.style.top = 110 + '%';
                chatc.style.left = (template.position.x * 100) + '%';

                const title = document.createElement("div");
                title.id = prefix + '-title';
                title.classList.add('CHAT-title');
                { // title block
                  const badge = document.createElement("div");
                  badge.id = prefix + '-badge';
                  badge.classList.add('CHAT-badge');
                  badge.style.background = template.badgeColor;
                  badge.innerHTML = template.initials;
                  title.appendChild(badge);

                  const sender = document.createElement("div");
                  sender.id = prefix + '-primaryText';
                  sender.classList.add(TemplateType.T_CHAT + '-sender');
                  title.appendChild(sender);
                }
                chatc.appendChild(title);

                const body = document.createElement("div");
                body.id = prefix + '-secondaryText';
                body.classList.add(TemplateType.T_CHAT + '-body');
                body.innerHtml = template.secondaryText;
                chatc.appendChild(body);

                ntContainer.appendChild(chatc);

                setTimeout(() => {

                  // chatc.style.transform = "translate3d("+ (template.position.x * width) +"px, " + (template.position.y * height) + "px, 0)";
                  chatc.style.top = ((template.position.y + deltaY) * 100) + '%';
                  chatc.style.left = (template.position.x * 100) + '%';
                }, 200);

              }
              container = chatc;
            }
          }


          if (container) {
            if (template.isVisible) {
              // <!> work on a copy of position.y to avoid resuse of new value
              let yposition = template.position.y;
              if (template.theme == '3') {
                yposition -= 0.002;
              }

              container.style.display = 'block';
              if (template.type !== TemplateType.T_NAMETAG && template.type !== TemplateType.T_CHAT) {
                container.style.top = (yposition * 100) + '%';
                container.style.left = (template.position.x * 100) + '%';
              } else if (template.type === TemplateType.T_NAMETAG) {
                container.style.top = (yposition * 100) + '%';
                container.style.left = (template.position.x * 100) + '%';
                // container.style.transform = "translate3d(" + (template.position.x * width) + "px, " + (yposition * height) + "px, 0)";
              }
              if (template.type !== TemplateType.T_CHAT) {
                container.style.borderLeftColor = template.borderColor || 'transparent';
                container.style.background = template.theme == '3' ? template.bgColor : 'transparent';
                const shadow = 5 * height / 720;
                container.style.boxShadow = template.theme == '3' ? `-${shadow}px ${shadow}px 0px ` + template.borderColor : 'unset';
                container.style.borderRadius = (template.theme == '3' ? shadow : 0) + 'px';
              }


              if (template.theme === '1') {
                container.classList.add(template.type + '1-container');
                container.classList.remove(template.type + '2-container');
                container.classList.remove(template.type + '3-container');
              } else if (template.theme === '2') {
                container.classList.add(template.type + '2-container');
                container.classList.remove(template.type + '1-container');
                container.classList.remove(template.type + '3-container');
              } else if (template.theme === '3') {
                container.classList.add(template.type + '3-container');
                container.classList.remove(template.type + '1-container');
                container.classList.remove(template.type + '2-container');
              }
            }
            // if (template.type === TemplateType.T_CHAT) {
            //   if (container) {
            //     container.style.transition = 'height 0ms';
            //     container.style.height = 0;
            //   }
            //   setTimeout(() => {
            //     if (container) {
            //       container.style.transition = 'height 200ms';
            //       container.style.height = 'auto';
            //     }
            //   }, 300);
            // }
          }
        }

        const secondaryText = document.getElementById(prefix + '-secondaryText');
        if (secondaryText) {
          if (template.theme === '1') {
            secondaryText.classList.add(template.type + '1-secondaryText');
            secondaryText.classList.remove(template.type + '2-secondaryText');
            secondaryText.classList.remove(template.type + '3-secondaryText');
          } else if (template.theme === '2') {
            secondaryText.classList.add(template.type + '2-secondaryText');
            secondaryText.classList.remove(template.type + '1-secondaryText');
            secondaryText.classList.remove(template.type + '3-secondaryText');
          } else if (template.theme === '3') {
            secondaryText.classList.add(template.type + '3-secondaryText');
            secondaryText.classList.remove(template.type + '1-secondaryText');
            secondaryText.classList.remove(template.type + '2-secondaryText');
          }

          secondaryText.innerHTML = template.secondaryText;
          if (template.type !== TemplateType.T_CHAT) {
            if (template.theme == '1') {


              if (template.type === TemplateType.T_LOWERTHIRD && template.isNew) {
                // console.log('theme change');
                container.style.width = '0';
                container.style.transition = 'width 0ms';

                secondaryText.style.paddingTop = '0px';
                secondaryText.style.paddingBottom = '0px';
                secondaryText.style.transition = 'height 0ms';
                secondaryText.style.height = '0vh';
              }
              setTimeout(() => {
                if (secondaryText) {
                  secondaryText.style.transition = 'height 150ms';
                  secondaryText.style.paddingTop = '3px';
                  secondaryText.style.paddingBottom = '3px';
                  secondaryText.style.height = '2.6vh';
                }
              }, 900);
            }

            if (template.theme == '2') {
              secondaryText.style.color = template.fgColor;
            } else {
              secondaryText.style.color = '#4a4a4a';
            }

            secondaryText.style.display = (!template.secondaryText || template.secondaryText.length === 0) ? 'none' : 'block';
            if (template.template === TemplateType.T_LOWERTHIRD && template.theme !== '1') {
              secondaryText.style.background = template.bgColor;
            }
          }

        }

        const primaryText = document.getElementById(prefix + '-primaryText');
        if (primaryText) {
          if (template.theme === '1') {
            primaryText.classList.add(template.type + '1-primaryText');
            primaryText.classList.remove(template.type + '2-primaryText');
            primaryText.classList.remove(template.type + '3-primaryText');
          } else if (template.theme === '2') {
            primaryText.classList.add(template.type + '2-primaryText');
            primaryText.classList.remove(template.type + '1-primaryText');
            primaryText.classList.remove(template.type + '3-primaryText');
          } else if (template.theme === '3') {
            primaryText.classList.add(template.type + '3-primaryText');
            primaryText.classList.remove(template.type + '1-primaryText');
            primaryText.classList.remove(template.type + '2-primaryText');
          }

          // check if text has an inlined paragraph
          const para = document.getElementById(prefix + '-primaryText-p');
          if (para) {
            if (template.type === TemplateType.T_TICKER && template.primaryText !== para.innerHTML) {
              // reset padding position
              leftPaddingRef.current = 60;
              para.style.transition = 'left 0ms linear';
              // para.style.left = '60px';
              para.style.transform = "translate3d(60px, 5px, 0)";

            }

            para.innerHTML = template.primaryText;
            if (template.type === TemplateType.T_LOWERTHIRD) {
              setTimeout(() => {
                container.style.display = 'block';
                container.style.width =
                  (20 + para.offsetWidth) + 'px';
              }, 100);
            }
          } else {
            primaryText.innerHTML = template.primaryText;

            if (template.type === TemplateType.T_LOWERTHIRD) {
              if (container) {
                if (template.isNew) {
                  // console.log('theme change');
                  container.style.width = '0';
                  container.style.transition = 'width 0ms';
                }
              }
              setTimeout(() => {
                if (primaryText && container) {
                  container.style.transition = 'width ' + Math.max(200, 400 + (primaryText.offsetWidth)) + 'ms';
                  container.style.width = (20 + Math.max(primaryText.offsetWidth, (secondaryText ? secondaryText.offsetWidth : 0))) + 'px';
                }
              }, 300);
            }
            primaryText.style.borderRadius = (template.theme == '3' ? 5 : 0) + 'px';

          }
          // console.log('NAMETAG fgcolor=', template.fgColor );
          primaryText.style.color = template.fgColor;
          primaryText.style.background = template.bgColor;
          primaryText.style.fontWeight = template.fontWeight;
        }

        const logoImg = document.getElementById(prefix + '-img');
        if (logoImg) {
          logoImg.style.backgroundImage = 'url(' + template.source + ')';
          logoImg.style.width = (128 * width / 1280) + 'px';
          logoImg.style.height = (128 * height / 720) + 'px';
        }

      }
    }
  }


  React.useEffect(() => {
    if (tickerIntervalRef.current) {
      clearInterval(tickerIntervalRef.current);
    }
    tickerIntervalRef.current = setInterval(() => {
      tickerAnimation();
    }, 1000);
  }, []);


  const tickerAnimation = useCallback(() => {

    const iframe: HTMLIFrameElement = document.getElementById('common_template') as HTMLIFrameElement;
    if (!iframe) {
      return;
    }
    const frameDocument = iframe.contentWindow.document;

    const tickerContainer = frameDocument.getElementById('TICKER-container');
    const tickerText = frameDocument.getElementById('TICKER-primaryText-p');
    if (!tickerContainer || tickerContainer.style.display === 'none') {
      leftPaddingRef.current = 60;
      if (tickerText) {
        // tickerText.style.left = '60px';
        tickerText.style.transform = "translate3d(60px, 0, 0)";
      }
      return;
    }
    const containerWidth = tickerContainer.offsetWidth;
    const tickerTextWidth = tickerText ? tickerText.offsetWidth : 0;


    if (tickerText) {

      // tickerText.style.left = '0px';


      if (tickerTextWidth > containerWidth) {
        let clPadding = leftPaddingRef.current;
        if (clPadding < -tickerTextWidth) {
          tickerText.style.transition = 'left 0ms linear, transform 0ms';

          clPadding = containerWidth + 60;
        } else {
          tickerText.style.transition = 'left 1003ms linear, transform 1003ms linear';
          clPadding = clPadding - 60;
        }
        tickerText.style.transform = "translate3d(" + clPadding + "px, 5px, 0)";
        // tickerText.style.left = clPadding + 'px';
        leftPaddingRef.current = clPadding;
        // console.log('currentLeftPadding', clPadding);

      } else {
        leftPaddingRef.current = 60;
        // center text
        tickerText.style.transition = 'left 100ms linear, transform 100ms';
        // tickerText.style.left = ((containerWidth - tickerTextWidth ) / 2) + 'px';
        tickerText.style.transform = "translate3d(" + ((containerWidth - tickerTextWidth) / 2) + "px, 5px, 0)";
      }
    }
  }, []);


  // const handleRunOutAnimation = () => {
  //   console.log('call runAnimationOUT');

  //   let iframeId = 'studio_common_template';
  //   const iframe = document.getElementById(iframeId) as HTMLIFrameElement;

  //   if (iframe) {

  //     const container = iframe.contentWindow.document.getElementById('container');
  //     if (container) {
  //       container.style.setProperty('opacity', '0.0');
  //       container.style.setProperty('width', '0');
  //     }
  //   }

  //   iframeRef.current.src = '';

  //   let oldtemplates = R.clone(templates);
  //   oldtemplates = oldtemplates.filter(tpl => tpl.sourceId !== templateInStore.id);

  //   console.log('SETTEMPLATES', oldtemplates);
  //   setTemplates(oldtemplates);
  //   debounce(apiService.saveChannel('iframes'), 100)();

  // }

  useEffect(() => {
    console.log('templates update', templatesInStore);
    handleRunInAnimation();
  }, [templatesInStore])

  useEffect(() => {
    // console.log('save channel: nameTags update');
    handleRunInAnimation();
  }, [nameTags])

  useEffect(() => {
    // console.log('save channel: publicChat update');
    handleRunInAnimation();
  }, [publicChats])

  // only 1 common template now
  return (
    <iframe key="common-template" title='common_template' id='common_template' className="template" src={`${process.env.PUBLIC_URL}/templates/common_template.html`} onLoad={handleRunInAnimation}></iframe>
  )
});  // end memo

// export const IFrames = () => {

//   const templatesInStore = useSelector((state: any) => state.templates);

//   const [apiService] = useState(container.resolve(ApiService));
//   const [templateLayer, setTemplateLayer] = useSharedState(apiService.templates$);

//   const dispatch = useDispatch();
//   let template = null;

//   useEffect(() => {
//     // read template from API and activate new
//     console.log('actives templates', templatesInStore);

//     // check if template is already in store
//     for (const tl of templateLayer) {
//       if (tl.type === TemplateType.T_LOWERTHIRD || tl.type === TemplateType.T_TICKER) {
//         template = tl;
//         const templateInStore = templatesInStore.find(t => t.id === tl.id);
//         if (templateInStore) {
//           const diff = apiService.notSameLayer(templateInStore, tl);
//           if (diff.length>0) {
//             dispatch({type: tl.type === TemplateType.T_LOWERTHIRD ? REPLACE_LOWERTHIRD_IN_TEMPLATES : REPLACE_TICKER_IN_TEMPLATES, payload: tl});
//           }
//         } else {
//           // tl not in store -> add it
//           dispatch({type: ADD_TEMPLATE, payload: tl});
//         }
//       }
//     }
//   }, [templateLayer]);

//   useEffect(() => {
//   }, [templatesInStore])

//   return (
//       <IFrame key="common-template" templates={templatesInStore}></IFrame>
//   );
// }